import { Component, computed, effect, inject, signal, OnInit } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { HOUR24_DATE_CONFIG, PATIENT_TYPE_OBSTETRIC } from 'src/app/calvin-emr-shared/global';
import { PatientDashboardTabComponent } from 'src/app/patients/dashboard-page/dashboard-tab/dashboard-tab.component';
import { Assessment } from 'src/app/calvin-emr-shared/models/assessment.model';
import { Patient } from 'src/app/calvin-emr-shared/models/patient.model';

// UI Modules
import { MenuModule } from 'primeng/menu';
import { PanelModule } from 'primeng/panel';
import { AccordionModule } from 'primeng/accordion';


// Assessment Info Components
import { GeneralAppearanceInfoComponent } from './general-appearance-info/general-appearance-info.component';
import { HEENTInfoComponent } from './heent-info/heent-info.component';
import { CardiovascularInfoComponent } from './cardiovascular-info/cardiovascular-info.component';
import { RespiratoryInfoComponent } from './respiratory-info/respiratory-info.component';
import { NeurologicalInfoComponent } from './neurological-info/neurological-info.component';
import { MusculoskeletalInfoComponent } from './musculoskeletal-info/musculoskeletal-info.component';
import { GastrointestinalInfoComponent } from './gastrointestinal-info/gastrointestinal-info.component';
import { GenitourinaryInfoComponent } from './genitourinary-info/genitourinary-info.component';
import { PsychosocialInfoComponent } from './psychosocial-info/psychosocial-info.component';
import { IntegumentaryInfoComponent } from './integumentary-info/integumentary-info.component';
import { PainAssessmentInfoComponent } from './pain-assessment-info/pain-assessment-info.component';
import { IntakeOutputInfoComponent } from './intake-output-info/intake-output-info.component';
import { ProviderNotifiedInfoComponent } from './provider-notified-info/provider-notified-info.component';
import { ExpansionPanelComponent } from "../../../shared/expansion-panel/expansion-panel.component";
import { AssessmentPdfGenerator } from './assessment-to-pdf';

@Component({
  selector: 'app-assessments-history-tab',
  templateUrl: './assessments-history-tab.component.html',
  styleUrls: ['./assessments-history-tab.component.scss'],
  standalone: true,
  imports: [
    MenuModule,
    PanelModule,
    AccordionModule,
    GeneralAppearanceInfoComponent,
    HEENTInfoComponent,
    CardiovascularInfoComponent,
    RespiratoryInfoComponent,
    NeurologicalInfoComponent,
    MusculoskeletalInfoComponent,
    GastrointestinalInfoComponent,
    GenitourinaryInfoComponent,
    PsychosocialInfoComponent,
    IntegumentaryInfoComponent,
    PainAssessmentInfoComponent,
    IntakeOutputInfoComponent,
    ProviderNotifiedInfoComponent,
    ExpansionPanelComponent
],
})
export class AssessmentsHistoryTabComponent extends PatientDashboardTabComponent implements OnInit {
  private patientSignal = this.patientStore.patient;
  selectedAssessment = signal<Assessment | null>(null);
  PATIENT_TYPE_OBSTETRIC = PATIENT_TYPE_OBSTETRIC;

  readonly menuItems = computed<MenuItem[]>(() => {
    const patient = this.patientSignal();
    if (!patient?.assessments) return [];

    const sorted = [...patient.assessments].sort(
      (a, b) => a.date.toDate().getTime() - b.date.toDate().getTime()
    );

    const grouped = new Map<string, Assessment[]>();
    sorted.forEach(asmt => {
      const d = asmt.date.toDate();
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const bucket = grouped.get(key) ?? [];
      bucket.push(asmt);
      grouped.set(key, bucket);
    });

    return [...grouped.entries()].map(([dateKey, dayAssessments]) => ({
      label: new Date(`${dateKey}T00:00:00`).toLocaleDateString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
      }),
      items: dayAssessments.map(asmt => ({
        label: asmt.date.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        icon: 'pi pi-download',
        iconPos: 'right',
        command: (event: any) => {
          if (event.originalEvent.target.classList.contains('pi-download')) {
            this.downloadAssessment(asmt, patient);
          } else {
            this.selectedAssessment.set(asmt);
          }
        }
      }))
    }));
  });

  constructor() {
    super();

    effect(
      () => {
        const patient = this.patientSignal();
        const assessments = patient?.assessments ?? [];

        if (assessments.length === 0) {
          this.selectedAssessment.set(null);
          return;
        }

        const sorted = [...assessments].sort(
          (a, b) => a.date.toDate().getTime() - b.date.toDate().getTime()
        );

        const current = this.selectedAssessment();
        if (!current) {
          this.selectedAssessment.set(sorted[sorted.length - 1]);
          return;
        }

        const currentTs = current.date.toDate().getTime();
        const stillExists = sorted.some((a) => a.date.toDate().getTime() === currentTs);

        if (!stillExists) {
          this.selectedAssessment.set(sorted[sorted.length - 1]);
        }
      },
    );
  }

  ngOnInit(): void { }

  downloadAssessment(assessment: Assessment, patient: Patient): void {
    const pdfGen = new AssessmentPdfGenerator(assessment, patient);
    const pdf = pdfGen.getResult();
    const dateStr = assessment.date.toDate().toLocaleDateString().replace(/\//g, '-');
    const fileName = `${patient.lastName}_${patient.firstName}_Assessment(${dateStr}).pdf`;
    pdf.save(fileName);
  }

  // --- GETTERS TO RESOLVE TEMPLATE ERRORS ---

  get patient(): Patient {
    return this.patientSignal()!;
  }

  get currentAsmt() {
    return this.selectedAssessment();
  }

  get hasAssessments(): boolean {
    return (this.patientSignal()?.assessments?.length ?? 0) > 0;
  }

  get selectedAssessmentDateString(): string {
    const asmt = this.currentAsmt;
    return asmt ? asmt.date.toDate().toLocaleString([], HOUR24_DATE_CONFIG).replace(',', ' at') : 'Loading...';
  }

  // Use '!' because these are guarded by @if (selectedAssessment()) in the HTML
  get generalAppearance() { return this.currentAsmt?.generalAppearance!; }
  get heent() { return this.currentAsmt?.heent!; }
  get cardiovascular() { return this.currentAsmt?.cardiovascular!; }
  get respiratory() { return this.currentAsmt?.respiratory!; }
  get neurological() { return this.currentAsmt?.neurological!; }
  get musculoskeletal() { return this.currentAsmt?.musculoskeletal!; }
  get gastrointestinal() { return this.currentAsmt?.gastrointestinal!; }
  get genitourinary() { return this.currentAsmt?.genitourinary!; }
  get psychosocial() { return this.currentAsmt?.psychosocial!; }
  get integumentary() { return this.currentAsmt?.integumentary!; }
  get painAssessment() { return this.currentAsmt?.painAssessment!; }
  get intakeOutput() { return this.currentAsmt?.intakeOutput!; }
  get providerNotified() { return this.currentAsmt?.providerNotified!; }
}